import { useIntl } from 'react-intl';
import { useCallback, useEffect, useState } from 'react';
import { Box, FormControlLabel, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PropTypes from 'prop-types';
import { Position } from '@xyflow/react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Checkbox from '@mui/material/Checkbox';
import { nanoid } from '@reduxjs/toolkit';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

export function SwitcherNodeForm({ id, data, onUpdateData }) {
  const ROW_HEIGHT = 30;
  const INITIAL_TOP_OFFSET = 120 + ROW_HEIGHT / 2;

  const { formatMessage: msg } = useIntl();
  const { conditions = [] } = data;

  const [currentConditions, setCurrentConditions] = useState(() => {
    if (conditions.length > 0) return conditions;
    return [
      { order: 0, defaultCondition: false, condition: '', handleId: `${id}-condition-${nanoid()}` }
    ];
  });

  const handleConditionChange = useCallback((index, value) => {
    setCurrentConditions((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], condition: value };
      return updated;
    });
  }, []);

  const handleIsDefaultChange = useCallback((index, checked) => {
    setCurrentConditions((prev) => {
      const updated = [...prev];
      updated.forEach((cond, i) => {
        updated[i] = {
          ...cond,
          defaultCondition: i === index ? checked : false,
          condition: i === index && checked ? '' : cond.condition
        };
      });
      return updated;
    });
  }, []);

  const handleAddCondition = useCallback(() => {
    setCurrentConditions((prev) => {
      const newIndex = prev.length;
      return [
        ...prev,
        {
          order: newIndex,
          condition: '',
          handleId: `${id}-condition-${nanoid()}`,
          defaultCondition: false
        }
      ];
    });
  }, [id]);

  const handleRemoveCondition = useCallback(
    (indexToRemove) => {
      if (currentConditions.length > 1) {
        setCurrentConditions((prev) => prev.filter((_, i) => i !== indexToRemove));
      }
    },
    [currentConditions]
  );

  useEffect(() => {
    const dynamicSourceHandles = currentConditions.map((conditionObj, index) => ({
      type: 'source',
      position: Position.Right,
      id: conditionObj.handleId,
      style: {
        top: `${INITIAL_TOP_OFFSET + (index - 1) * ROW_HEIGHT + (index === currentConditions.length ? ROW_HEIGHT / 2 : 0)}px`
      }
    }));

    onUpdateData?.({
      id,
      data: {
        ...data,
        conditions: currentConditions,
        handleConf: [...dynamicSourceHandles]
      }
    });
  }, [INITIAL_TOP_OFFSET, currentConditions, data, id, onUpdateData]);

  return (
    <Stack spacing={1.5} sx={{ p: 1 }}>
      <Box
        sx={{
          borderRadius: 2,
          p: 2,
          backgroundColor: '#fafafa55',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}
      >
        <Stack spacing={0}>
          {currentConditions.map((conditionObj, index) => (
            <Box
              key={conditionObj.handleId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'white',
                borderRadius: 1,
                gap: 0,
                m: 0,
                border: 0,
                '&:hover': {
                  backgroundColor: '#f8f9fa',
                  borderColor: '#d0d7de'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <Stack sx={{ flexGrow: 1, gap: 1, p: 0.5 }} spacing={0}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MDInput
                    id={`inputCondition${index}`}
                    type="text"
                    value={conditionObj.condition}
                    placeholder={
                      !conditionObj.defaultCondition
                        ? msg({ id: 'field.switcher.condition.placeholder' }, { index: index + 1 })
                        : ''
                    }
                    onChange={(e) => handleConditionChange(index, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCondition();
                      }
                    }}
                    sx={{
                      flexGrow: 1,
                      border: 'none',
                      outline: 'none',
                      backgroundColor: 'transparent'
                    }}
                    disabled={conditionObj.defaultCondition}
                  />
                  <Tooltip title={msg({ id: 'tooltip.delete' })}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveCondition(index)}
                      sx={{ p: 0.5 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {index === currentConditions.length - 1 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={conditionObj.defaultCondition}
                        onChange={(e) => handleIsDefaultChange(index, e.target.checked)}
                      />
                    }
                    label={msg({ id: 'field.switcher.default.condition.label' })}
                    sx={{ m: 0 }}
                  />
                )}
              </Stack>
            </Box>
          ))}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <MDButton
              id="configureBtn"
              size="small"
              color="primary"
              variant="gradient"
              startIcon={<AddIcon />}
              onClick={handleAddCondition}
              sx={{ borderRadius: 2, px: 3, py: 1 }}
            >
              {msg({ id: 'button.add' })}
            </MDButton>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
}

SwitcherNodeForm.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.shape({
    conditions: PropTypes.arrayOf(
      PropTypes.shape({
        condition: PropTypes.string.isRequired,
        handleId: PropTypes.string.isRequired,
        order: PropTypes.number,
        defaultCondition: PropTypes.bool
      })
    ),
    handleConf: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(['source', 'target']).isRequired,
        position: PropTypes.oneOf([Position.Top, Position.Right, Position.Bottom, Position.Left])
      })
    )
  }),
  onUpdateData: PropTypes.func.isRequired
};
